import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import "./patientInbody.css";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import Toast from "../../../components/layout/toast/Toast";
import { Col, Container, Row } from "react-bootstrap";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const getDynamicsModalFields = (t) => [
  { name: "weight", label: t("inbody.fields.weight"), type: "number" },
  { name: "bmi", label: t("inbody.fields.bmi"), type: "number" },
  {
    name: "intracellular_water",
    label: t("inbody.fields.intracellularWater"),
    type: "number",
  },
  { name: "extracellular_water", label: t("inbody.fields.extracellularWater"), type: "number" },
  { name: "visceral_fat_level", label: t("inbody.fields.visceralFatLevel"), type: "number" },
  // { name: "fat_percentage", label: "Жир (%)", type: "number" },
  { name: "body_fat_content", label: t("inbody.fields.bodyFatContent"), type: "number" },
  { name: "muscle_mass", label: t("inbody.fields.muscleMass"), type: "number" },

  {
    name: "skeletal_muscle_mass",
    label: t("inbody.fields.skeletalMuscleMass"),
    type: "number",
  },
  { name: "files", label: t("inbody.fields.file"), type: "file", multiple: true },
];

const getInitialDynamicsData = (fields) =>
  Object.fromEntries(
    fields.map((f) => [f.name, f.type === "file" ? [] : ""]),
  );

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const normalizeDisplayValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

const toNumeric = (value) =>
  value != null && value !== "" && !Number.isNaN(Number(value))
    ? Number(value)
    : null;

const getIcwToTotalWaterRatio = (intracellularWater, extracellularWater) => {
  const icw = toNumeric(intracellularWater);
  const ecw = toNumeric(extracellularWater);
  if (icw == null || ecw == null) return null;

  const totalWater = icw + ecw;
  if (totalWater <= 0) return null;

  return icw / totalWater;
};

const rowCanDecodeWithAi = (row) =>
  Boolean(
    row?.has_file
    || (Array.isArray(row?.files) && row.files.length > 0)
    || (Array.isArray(row?.file_ids) && row.file_ids.length > 0)
    || Number(row?.id_files) > 0,
  );

const buildDynamicsChanges = (before, after, fields) =>
  fields
    .filter((field) => field.type !== "file")
    .reduce((acc, field) => {
    const fromValue = normalizeComparableValue(before?.[field.name]);
    const toValue = normalizeComparableValue(after?.[field.name]);
    if (fromValue !== toValue) {
      acc.push({
        label: field.label,
        from: before?.[field.name],
        to: after?.[field.name],
      });
    }
    return acc;
    }, []);

function PatientInbody() {
  const { t } = useTranslation("patient");
  const dynamicsModalFields = useMemo(() => getDynamicsModalFields(t), [t]);
  const initialDynamicsData = useMemo(
    () => getInitialDynamicsData(dynamicsModalFields),
    [dynamicsModalFields],
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDynamics, setEditingDynamics] = useState(null);
  const [dynamics, setDynamics] = useState([]);
  const [toast, setToast] = useState({});
  const [decodingId, setDecodingId] = useState(null);

  const { patientId } = useParams();
  const id_patient = Number(patientId);

  const fetchDynamics = async (id_patient) => {
    const data = await GetData({
      module: "patient",
      block: "dynamics",
      id_patient: id_patient,
    });

    if (data && Array.isArray(data.result)) {
      setDynamics(data.result);
    } else if (data && data.result) {
      setDynamics([data.result]);
    } else {
      setDynamics([]);
    }
  };

  useEffect(() => {
    fetchDynamics(id_patient);
  }, [id_patient]);

  const blockTitleButtons = [
    { label: t("analyses.buttons.add"), onClick: () => setShowAddModal(true), type: "light" },
  ];

  const showToast = (title, status = "success") => {
    setToast({
      id: Date.now(),
      title,
      status,
    });
  };

  const handleSaveDynamics = async (formData) => {
    try {
      const files = Array.isArray(formData?.files) ? formData.files : [];
      const requestData = { ...(formData || {}) };
      delete requestData.files;
      const response = await GetData({
        module: "patient",
        block: "dynamics",
        action: "add",
        id_patient: id_patient,
        ...requestData,
      }, files);

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("inbody.toast.addFailed"), "error");
        return;
      }

      await logPatientChange({
        idPatient: id_patient,
        text: buildPatientLogText("patient_dynamics_added", {
          details: dynamicsModalFields
            .filter((field) => field.type !== "file")
            .map((field) => `${field.label}: ${normalizeDisplayValue(formData?.[field.name])}`)
            .join("; "),
        }),
      });
      setShowAddModal(false);
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("inbody.toast.added"), "success");
    } catch (err) {
      console.error("Ошибка сохранения оценки состава тела:", err);
      showToast(t("inbody.toast.addFailed"), "error");
    }
  };

  const openEditDynamicsModal = (row) => {
    setEditingDynamics(row);
    setShowEditModal(true);
  };

  const handleUpdateDynamics = async (formData) => {
    if (!editingDynamics?.id) return;

    try {
      const files = Array.isArray(formData?.files) ? formData.files : [];
      const requestData = { ...(formData || {}) };
      delete requestData.files;
      const response = await GetData({
        module: "patient",
        block: "dynamics",
        action: "update",
        id_patient: id_patient,
        id_dynamics: editingDynamics.id,
        id: editingDynamics.id,
        ...requestData,
      }, files);

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("inbody.toast.updateFailed"), "error");
        return;
      }

      await logPatientChange({
        idPatient: id_patient,
        idItem: editingDynamics.id,
        text: buildPatientLogText("patient_dynamics_updated", {
          changes: buildDynamicsChanges(editingDynamics, formData, dynamicsModalFields),
        }),
      });
      setShowEditModal(false);
      setEditingDynamics(null);
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("inbody.toast.updated"), "success");
    } catch (err) {
      console.error("Ошибка обновления оценки состава тела:", err);
      showToast(t("inbody.toast.updateFailed"), "error");
    }
  };

  const runDecodeWithAi = async (row, files = []) => {
    const rowId = Number(row?.id);
    if (!rowId || !id_patient) return;

    setDecodingId(rowId);
    try {
      const response = await GetData(
        {
          module: "patient",
          block: "dynamics",
          action: "decode_ai",
          id_patient,
          id_dynamics: rowId,
          id: rowId,
        },
        files.length > 0 ? files : undefined,
      );

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("inbody.toast.decodeFailed"), "error");
        return;
      }

      if (response?.result) {
        const updated = response.result;
        setDynamics((prev) =>
          prev.map((item) => (Number(item?.id) === rowId ? { ...item, ...updated } : item)),
        );
      } else {
        await fetchDynamics(id_patient);
      }

      showToast(response?.status?.message || t("inbody.toast.decoded"), "success");
    } catch (err) {
      console.error("Ошибка расшифровки InBody:", err);
      showToast(t("inbody.toast.decodeFailed"), "error");
    } finally {
      setDecodingId(null);
    }
  };

  const handleDecodeWithAi = (row) => {
    const rowId = Number(row?.id);
    if (!rowId || !id_patient || decodingId) return;

    if (rowCanDecodeWithAi(row)) {
      runDecodeWithAi(row);
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.jpg,.jpeg,.png,.webp,image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener(
      "change",
      () => {
        const files = Array.from(input.files ?? []);
        input.remove();
        if (!files.length) return;
        runDecodeWithAi(row, files);
      },
      { once: true },
    );
    input.click();
  };

  const handleDeleteDynamics = async (rowId) => {
    if (!rowId) return;

    const isConfirmed = window.confirm(t("inbody.confirmDelete"));
    if (!isConfirmed) return;

    try {
      const response = await GetData({
        module: "patient",
        block: "dynamics",
        action: "delete",
        id_patient: id_patient,
        id_dynamics: rowId,
        id: rowId,
      });

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("inbody.toast.deleteFailed"), "error");
        return;
      }

      await logPatientChange({
        idPatient: id_patient,
        idItem: rowId,
        text: buildPatientLogText("patient_dynamics_deleted", {
          details: ``,
        }),
      });
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("inbody.toast.deleted"), "success");
    } catch (err) {
      console.error("Ошибка удаления оценки состава тела:", err);
      showToast(t("inbody.toast.deleteFailed"), "error");
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "—";

    const [datePart, timePart] = dateTimeStr.split(" ");
    if (!datePart) return dateTimeStr;

    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return dateTimeStr;

    return `${day}.${month}.${year} ${timePart || ""}`.trim();
  };

  const formatShortDate = (dateTimeStr) => {
    if (!dateTimeStr) return "—";
    const [datePart] = String(dateTimeStr).split(" ");
    const [year, month, day] = String(datePart).split("-");
    if (!year || !month || !day) return String(dateTimeStr);
    return `${day}.${month}`;
  };

  const sortedDynamics =
    dynamics && dynamics.length
      ? [...dynamics].sort((a, b) => {
          const ad = a?.date_created ? Date.parse(a.date_created) : NaN;
          const bd = b?.date_created ? Date.parse(b.date_created) : NaN;
          if (!Number.isNaN(ad) && !Number.isNaN(bd)) return bd - ad; // новые сверху
          return 0;
        })
      : [];

  const latestDynamics = sortedDynamics.length ? sortedDynamics[0] : null;

  const chartData = sortedDynamics
    .slice()
    .reverse()
    .map((d) => ({
      id: d?.id ?? d?.date_created,
      date_created: d?.date_created,
      dateLabel: formatShortDate(d?.date_created),
      weight: toNumeric(d?.weight),
      fat_percentage:
        d?.fat_percentage != null && d?.fat_percentage !== ""
          ? Number(d.fat_percentage)
          : null,
      muscle_mass:
        d?.muscle_mass != null && d?.muscle_mass !== ""
          ? Number(d.muscle_mass)
          : null,
      skeletal_muscle_mass: toNumeric(d?.skeletal_muscle_mass),
      body_fat_content: toNumeric(d?.body_fat_content),
      visceral_fat_level: toNumeric(d?.visceral_fat_level),
      icw_total_water_ratio:
        getIcwToTotalWaterRatio(
          d?.intracellular_water,
          d?.extracellular_water,
        ) ?? toNumeric(d?.intracellular_water),
    }))
    .filter((row) => row.date_created);

  return (
    <div className="patient-dynamics">
      {toast.title ? <Toast key={toast.id} title={toast.title} status={toast.status} /> : false}

      <div className="patient-name-title-sticky">
        <BlockTitle title={t("inbody.title")} buttons={blockTitleButtons} />
      </div>


      <Row className="mt-4">
        <Col>
          <div className="history-section">
            {chartData.length >= 2 && (
              <div className="chart-container main-chart">
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="dateLabel"
                        tick={{ fontSize: 12 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        yAxisId="kg"
                        tick={{ fontSize: 12 }}
                        width={44}
                        tickFormatter={(v) => (v == null ? "" : v)}
                      />
                      <YAxis
                        yAxisId="pct"
                        orientation="right"
                        tick={{ fontSize: 12 }}
                        width={44}
                        tickFormatter={(v) => (v == null ? "" : v)}
                      />
                      <YAxis yAxisId="level" hide />
                      <Tooltip
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.date_created
                            ? formatDateTime(payload[0].payload.date_created)
                            : "—"
                        }
                        formatter={(value, name) => {
                          if (value == null || Number.isNaN(Number(value))) return ["—", name];
                          if (name === t("inbody.chart.icwRatio")) return [Number(value).toFixed(3), name];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="kg"
                        type="monotone"
                        dataKey="weight"
                        name={t("inbody.fields.weight")}
                        stroke="#1c7ed6"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                      {/* <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="fat_percentage"
                        name="Жир (%)"
                        stroke="#ff6b6b"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      /> */}
                      <Line
                        yAxisId="kg"
                        type="monotone"
                        dataKey="muscle_mass"
                        name={t("inbody.fields.muscleMass")}
                        stroke="#2f9e44"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                      <Line
                        yAxisId="kg"
                        type="monotone"
                        dataKey="skeletal_muscle_mass"
                        name={t("inbody.fields.skeletalMuscleMass")}
                        stroke="#1971c2"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                      <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="body_fat_content"
                        name={t("inbody.fields.bodyFatContent")}
                        stroke="#f08c00"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                      <Line
                        yAxisId="pct"
                        type="monotone"
                        dataKey="icw_total_water_ratio"
                        name={t("inbody.chart.icwRatio")}
                        stroke="#7048e8"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                      <Line
                        yAxisId="level"
                        type="monotone"
                        dataKey="visceral_fat_level"
                        name={t("inbody.fields.visceralFatLevel")}
                        stroke="#c2255c"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>{t("inbody.columns.date")}</th>
                    <th>{t("inbody.columns.weight")}</th>
                    <th>{t("inbody.columns.bmi")}</th>
                    {/* <th>Протеин</th> */}
                    {/* <th>Минералы</th> */}
                    <th>{t("inbody.columns.icwRatio")}</th>
                    <th>{t("inbody.columns.visceralFatLevel")}</th>
                    {/* <th>Жир (%)</th> */}
                    <th>{t("inbody.columns.bodyFatContent")}</th>
                    <th>{t("inbody.columns.muscleMass")}</th>
                    <th>{t("inbody.columns.skeletalMuscleMass")}</th>
                    {/* <th>Оценка</th> */}
                    <th>{t("inbody.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDynamics.length ? (
                    sortedDynamics.map((row) => {
                      const isLatest = String(row?.id) === String(latestDynamics?.id);
                      const isDecodingRow = Number(decodingId) === Number(row?.id);
                      return (
                        <tr
                          key={row?.id ?? row?.date_created}
                          className="history-row"
                        >
                          <td>
                            {formatDateTime(row?.date_created)}
                            {isLatest && <span className="current-badge">{t("inbody.latest")}</span>}
                          </td>
                          <td>{row?.weight ?? "—"}</td>
                          <td>{row?.bmi ?? "—"}</td>
                          {/* <td>{row?.protein ?? "—"}</td> */}
                          {/* <td>{row?.minerals ?? "—"}</td> */}
                          <td>
                            {(
                              getIcwToTotalWaterRatio(
                                row?.intracellular_water,
                                row?.extracellular_water,
                              ) ?? toNumeric(row?.intracellular_water)
                            )?.toFixed(3) ?? "—"}
                          </td>
                          <td>{row?.visceral_fat_level ?? "—"}</td>
                          {/* <td>{row?.fat_percentage ?? "—"}</td> */}
                          <td>{row?.body_fat_content ?? "—"}</td>
                          <td>{row?.muscle_mass ?? "—"}</td>
                          <td>{row?.skeletal_muscle_mass ?? "—"}</td>
                          {/* <td className="score-cell">{row?.score ?? "—"}</td> */}
                          <td className="dynamics-actions-cell">
                            <div
                              className="dynamics-row-actions"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="dynamics-action-btn dynamics-action-btn--decode"
                                onClick={() => handleDecodeWithAi(row)}
                                disabled={isDecodingRow}
                                title={t("inbody.decodeTitle")}
                                aria-label={t("inbody.decodeAria")}
                              >
                                {isDecodingRow ? (
                                  <span className="dynamics-action-spinner" aria-hidden="true" />
                                ) : (
                                  t("analyses.buttons.ai")
                                )}
                              </button>
                              <button
                                type="button"
                                className="dynamics-action-btn dynamics-action-btn--edit"
                                onClick={() => openEditDynamicsModal(row)}
                                title={t("inbody.edit")}
                                aria-label={t("inbody.edit")}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="dynamics-action-btn dynamics-action-btn--delete"
                                onClick={() => handleDeleteDynamics(row?.id)}
                                title={t("inbody.delete")}
                                aria-label={t("inbody.delete")}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M6 7h12v2H6V7zm2 3h8l-1 10H9L8 10zm3-6h2l1 1h4v2H6V5h4l1-1z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        {t("inbody.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      <EditableModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        title={t("inbody.addTitle")}
        initialData={initialDynamicsData}
        fields={dynamicsModalFields}
        onSave={handleSaveDynamics}
        closeOnSave={false}
      />

      <EditableModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingDynamics(null);
        }}
        title={t("inbody.editTitle")}
        initialData={
          editingDynamics
            ? Object.fromEntries(
                dynamicsModalFields.map((f) => [
                  f.name,
                  f.type === "file" ? [] : (editingDynamics?.[f.name] ?? ""),
                ]),
              )
            : initialDynamicsData
        }
        fields={dynamicsModalFields}
        onSave={handleUpdateDynamics}
        closeOnSave={false}
      />
    </div>
  );
}

export default PatientInbody;
